// Divisão da conta no Fechar mesa. Tudo em centavos por dentro: a soma das
// partes precisa bater EXATAMENTE com o total, senão sobra "Restante R$ 0,01"
// e a mesa não fecha sozinha.
import { medidaDoItem } from "../../../lib/unidadeVenda";

const centavos = (v) => Math.round((Number(v) || 0) * 100);
const reais = (c) => Math.round(c) / 100;
const somaC = (lista) => lista.reduce((s, v) => s + centavos(v), 0);

export const valorItem = (item) =>
  (item.final_price ?? item.sale_price ?? 0) * (item.quantity || 1);

export const chaveItem = (item) => item.cartItemId || `i${item._idx}`;

// Pago por divisão num produto que ainda não quitou (o quitado conta cheio)
export const pagoParcialItem = (item) => (item._paid ? 0 : Number(item._pagoParcial) || 0);

export const restanteItem = (item) =>
  item._paid ? 0 : Math.max(0, valorItem(item) - (Number(item._pagoParcial) || 0));

const precoUnitario = (item) => item.final_price ?? item.sale_price ?? 0;

// Quilo/litro (30/09/2026): produto por peso (ou com quantidade quebrada) não
// se paga por unidade — 1,250 kg virava "Pagar quantas? 1 de 2". É uma parte
// só: marcado, paga o que falta dele inteiro (como o cancelar em parte, que
// também não divide peso).
const pagaInteiro = (item) =>
  !!medidaDoItem(item) || !Number.isInteger(Number(item.quantity ?? item.quantidade ?? 1));

// Unidades que ainda faltam pagar (3× Coca com 1 paga → 2). Parte de divisão
// pode deixar fração de unidade: arredonda pra cima para ela poder ser marcada.
export function unidadesRestantes(item) {
  const unit = precoUnitario(item);
  if (item._paid || !(unit > 0)) return 0;
  if (pagaInteiro(item)) return restanteItem(item) >= 0.005 ? 1 : 0;
  return Math.ceil(restanteItem(item) / unit - 0.001);
}

// `_selQtd` vazio = todas as unidades que faltam
export function valorSelecionado(item) {
  if (!item._selected || item._paid) return 0;
  const k = Number(item._selQtd) || 0;
  if (!k || k >= unidadesRestantes(item)) return restanteItem(item);
  return Math.min(restanteItem(item), reais(centavos(precoUnitario(item)) * k));
}

/**
 * Rodada sem divisão: produto marcado inteiro quita; marcado só em parte das
 * unidades ("1 de 3 Cocas") fica com o valor pago e continua em aberto.
 */
export function pagarSelecionados(items, { forma, formaCompleta, pagamentoId }) {
  return items.map((item) => {
    if (!item._selected || item._paid) return item;
    const valor = valorSelecionado(item);
    const limpo = { ...item, _selected: false, _selQtd: null };
    if (valor >= restanteItem(item) - 0.005) return { ...limpo, _paid: true, _paymentInfo: formaCompleta };
    const k = Number(item._selQtd) || 0;
    return {
      ...limpo,
      _pagoParcial: reais(centavos(item._pagoParcial) + centavos(valor)),
      _histParcial: [...(item._histParcial || []), { label: forma, valor, parte: `${k} un`, pagamento: pagamentoId }],
    };
  });
}

/**
 * Valor digitado à mão com produtos marcados (dono, 24/09/2026): "o produto é
 * R$ 22, escrevo R$ 10" paga só os R$ 10, repartidos nos marcados na proporção
 * do que cada um ia pagar. O produto continua em aberto com "Pago R$ 10,00".
 * Valor que cobre tudo é o pagamento normal (quita os marcados).
 */
export function pagarValorNosSelecionados(items, valor, { forma, formaCompleta, pagamentoId }) {
  const marcados = items.filter((i) => i._selected && !i._paid);
  const totalC = somaC(marcados.map(valorSelecionado));
  const valorC = centavos(valor);
  if (valorC >= totalC) return pagarSelecionados(items, { forma, formaCompleta, pagamentoId });
  // Centavos: proporcional arredondado, a sobra no último que ainda cabe
  const partes = marcados.map((i) => Math.min(centavos(valorSelecionado(i)), Math.round((centavos(valorSelecionado(i)) * valorC) / totalC)));
  let sobra = valorC - partes.reduce((s, c) => s + c, 0);
  for (let j = partes.length - 1; j >= 0 && sobra !== 0; j--) {
    const cabe = sobra > 0 ? centavos(valorSelecionado(marcados[j])) - partes[j] : -partes[j];
    const d = sobra > 0 ? Math.min(sobra, cabe) : Math.max(sobra, cabe);
    partes[j] += d;
    sobra -= d;
  }
  const porItem = new Map(marcados.map((i, j) => [i, partes[j]]));
  return items.map((item) => {
    if (!porItem.has(item)) return item;
    const c = porItem.get(item);
    const limpo = { ...item, _selected: false, _selQtd: null };
    if (c <= 0) return limpo;
    return {
      ...limpo,
      _pagoParcial: reais(centavos(item._pagoParcial) + c),
      _histParcial: [...(item._histParcial || []), { label: forma, valor: reais(c), pagamento: pagamentoId }],
    };
  });
}

// 10,00 ÷ 3 → 3,33 + 3,33 + 3,34: a sobra de centavos vai na última parte
export function dividirValor(total, partes) {
  const n = Math.max(1, Math.floor(Number(partes) || 1));
  const totalC = centavos(total);
  const base = Math.floor(totalC / n);
  return Array.from({ length: n }, (_, i) => reais(i === n - 1 ? totalC - base * (n - 1) : base));
}

/**
 * `bruto` pode ser menor que a soma do que falta nos produtos (mesa com
 * pagamento "por valor" já feito): cada produto entra na divisão com a mesma
 * proporção, para a divisão cobrir só o que realmente falta.
 */
export function criarDivisao({ itens, bruto, liquido, partes, mesaInteira, desconto }) {
  // Marcado só em parte das unidades entra na divisão só com essas unidades
  const cobrir = (i) => (i._selected ? valorSelecionado(i) : restanteItem(i));
  const soma = itens.reduce((s, i) => s + cobrir(i), 0);
  const escala = soma > 0 ? Math.min(1, bruto / soma) : 0;
  const brutoC = centavos(Math.min(bruto, soma));
  const base = {};
  const quita = {};
  let acumulado = 0;
  itens.forEach((item, idx) => {
    const c = idx === itens.length - 1
      ? Math.max(0, brutoC - acumulado)
      : Math.round(centavos(cobrir(item)) * escala);
    base[chaveItem(item)] = reais(c);
    // Quita no fim quem entrou com tudo o que faltava (mesmo reduzido pela escala)
    quita[chaveItem(item)] = cobrir(item) >= restanteItem(item) - 0.005;
    acumulado += c;
  });
  return {
    // Identifica a divisão no log: apagar uma parte paga reabre ESTA divisão
    id: `d${Date.now().toString(36)}`,
    base,
    quita,
    partes,
    mesaInteira,
    valores: dividirValor(liquido, partes),
    pagas: 0,
    bruto: reais(brutoC),
    liquido: reais(centavos(liquido)),
    desconto: desconto || null,
  };
}

// O que cada produto já recebeu nesta divisão. O mapa só vale se cobre as
// partes pagas de agora (`pagoPorItemPagas`): divisão gravada antes de
// 24/09/2026 — ou com uma parte paga por uma aba com o painel antigo, que não
// atualiza o mapa — sai do acumulado das partes pagas (dá o mesmo).
export function pagoNaDivisao(divisao) {
  if (divisao.pagoPorItem && divisao.pagoPorItemPagas === divisao.pagas) return divisao.pagoPorItem;
  const liqC = centavos(divisao.liquido);
  const antesC = somaC(divisao.valores.slice(0, divisao.pagas));
  const out = {};
  Object.entries(divisao.base).forEach(([chave, v]) => {
    out[chave] = liqC > 0 ? reais(Math.round((centavos(v) * antesC) / liqC)) : 0;
  });
  return out;
}

// Quanto do valor BRUTO de cada produto a parte `k` cobre: o acumulado das
// partes até ela, menos o que o produto já recebeu na divisão. A última fecha
// o produto sem sobra — mesmo com parte editada ou parte paga apagada.
// O total da parte sai exato (R$ 10 pagos = R$ 10 nos produtos); os centavos
// que sobram do arredondamento vão para os produtos com a maior fração.
function alocarParte(divisao, k) {
  const liqC = centavos(divisao.liquido);
  const depois = somaC(divisao.valores.slice(0, k + 1));
  const pago = pagoNaDivisao(divisao);
  const chaves = Object.keys(divisao.base);
  const falta = chaves.map((ch) => Math.max(0, centavos(divisao.base[ch]) - centavos(pago[ch])));
  const fracao = liqC > 0 ? Math.min(1, depois / liqC) : 1;
  const ideais = chaves.map((ch, j) => Math.min(falta[j], Math.max(0, centavos(divisao.base[ch]) * fracao - centavos(pago[ch]))));
  const partes = ideais.map(Math.floor);
  const alvoC = Math.max(0, Math.round(somaC(Object.values(divisao.base)) * fracao) - somaC(chaves.map((ch) => pago[ch])));
  let resto = alvoC - partes.reduce((s, c) => s + c, 0);
  const ordem = chaves.map((_, j) => j).sort((a, b) => (ideais[b] - partes[b]) - (ideais[a] - partes[a]));
  for (let n = 0; resto !== 0 && n < ordem.length * 2; n++) {
    const j = ordem[n % ordem.length];
    if (resto > 0 && partes[j] < falta[j]) { partes[j] += 1; resto -= 1; }
    else if (resto < 0 && partes[j] > 0) { partes[j] -= 1; resto += 1; }
  }
  const aloc = {};
  chaves.forEach((ch, j) => { aloc[ch] = reais(partes[j]); });
  return aloc;
}

export function parteAtual(divisao) {
  const k = divisao.pagas;
  const valor = divisao.valores[k] || 0;
  const bruto = reais(Object.values(alocarParte(divisao, k)).reduce((s, v) => s + centavos(v), 0));
  const desconto = divisao.desconto ? Math.max(0, reais(centavos(bruto) - centavos(valor))) : 0;
  return { numero: k + 1, total: divisao.partes, valor, bruto: divisao.desconto ? bruto : valor, desconto };
}

/** Registra uma parte paga nos produtos da divisão. Na última, os produtos quitam. */
export function aplicarParte(items, divisao, label, pagamentoId) {
  const aloc = alocarParte(divisao, divisao.pagas);
  const pago = pagoNaDivisao(divisao);
  const pagoPorItem = {};
  Object.keys(divisao.base).forEach((chave) => {
    pagoPorItem[chave] = reais(centavos(pago[chave]) + centavos(aloc[chave]));
  });
  const parte = `${divisao.pagas + 1}/${divisao.partes}`;
  const concluida = divisao.pagas + 1 >= divisao.partes;
  const novos = items.map((item) => {
    const chave = chaveItem(item);
    if (item._paid || !(chave in divisao.base)) return item;
    const valor = aloc[chave] || 0;
    const hist = [...(item._histParcial || []), { label, valor, parte, pagamento: pagamentoId }];
    const pago = reais(centavos(item._pagoParcial) + centavos(valor));
    const atualizado = { ...item, _selected: false, _selQtd: null, _pagoParcial: pago, _histParcial: hist };
    return concluida && divisao.quita?.[chave] !== false
      ? { ...atualizado, _paid: true, _paymentInfo: `Dividido em ${divisao.partes}` }
      : atualizado;
  });
  return {
    items: novos,
    divisao: concluida ? null : { ...divisao, pagas: divisao.pagas + 1, pagoPorItem, pagoPorItemPagas: divisao.pagas + 1 },
    parte,
  };
}

// Desconto que entrou nas partes já pagas. O pedido só recebe o desconto no
// fechamento; sem somar as partes, ficaria registrado só o da última rodada.
export function somarDesconto(acumulado, valor, meta) {
  if (!(valor > 0)) return acumulado || null;
  return {
    ...(acumulado || {}),
    ...(meta || {}),
    valor: reais(centavos(acumulado?.valor) + centavos(valor)),
  };
}

export function serializarParciais(items) {
  const out = {};
  items.forEach((item) => {
    if ((Number(item._pagoParcial) || 0) > 0) {
      out[chaveItem(item)] = { valor: item._pagoParcial, hist: item._histParcial || [] };
    }
  });
  return out;
}

export function restaurarParcial(item, parciais) {
  const salvo = parciais?.[chaveItem(item)];
  if (!salvo) return item;
  return { ...item, _pagoParcial: Number(salvo.valor) || 0, _histParcial: salvo.hist || [] };
}

/**
 * O que fica gravado em `mesa.pagamentos` — sobrevive a fechar o popup.
 * `descontoAcumulado`: desconto já concedido na sessão (partes pagas de divisão
 * e o da tela concedido ao lançar um pagamento — `lib/rodada.js`), em R$.
 * `descontoReaberto`: só quando a tela decide sobre ele (`null` = o desconto
 * da reabertura foi concedido e entrou no acumulado; a mesa não o repõe mais).
 * Sem a chave, o servidor repõe o gravado (`_manter_mesa_reaberta`).
 */
export function montarPagamentos({ log, partialPaid, items, divisao, descontoAcumulado, descontoReaberto }) {
  return {
    log,
    partial_paid: partialPaid,
    paid_items: items.filter((i) => i._paid).map((i) => ({ key: chaveItem(i), label: i._paymentInfo || '' })),
    parciais: serializarParciais(items),
    divisao: divisao || null,
    desconto_acumulado: descontoAcumulado || null,
    ...(descontoReaberto !== undefined ? { desconto_reaberto: descontoReaberto } : {}),
  };
}

/**
 * Desconto que vai para o pedido ao fechar a mesa: o da rodada atual (fora de
 * divisão) somado ao que já entrou nas partes pagas de divisões.
 * `rodada` = { valor, tipo, percentual, cupom }.
 */
export function descontoDoFechamento(rodada, acumulado) {
  const total = reais(centavos(rodada?.valor) + centavos(acumulado?.valor));
  const meta = (rodada?.valor || 0) > 0 ? rodada : (acumulado || {});
  return { total, tipo: meta.tipo, percentual: meta.percentual ?? null, cupom: meta.cupom || null };
}

/**
 * Quanto do desconto do fechamento cada pedido da conta recebe.
 * `totais`: total de cada pedido aberto, na ordem do PUT.
 *
 * O desconto ia inteiro para um pedido só; maior que ele, o pedido zerava e o
 * resto sumia — a soma dos pedidos ficava maior que o valor pago, e a nota da
 * conta era recusada por não fechar (achado 429). Agora o maior pedido recebe
 * primeiro (quase sempre cabe nele: continua um aviso de desconto só) e o que
 * não couber passa para o seguinte.
 */
export function descontoPorPedido(desconto, totais) {
  const t = (totais || []).map(centavos);
  const partes = t.map(() => 0);
  let falta = centavos(desconto);
  const ordem = t.map((_, i) => i).sort((a, b) => t[b] - t[a] || a - b);
  for (const i of ordem) {
    if (falta <= 0) break;
    partes[i] = Math.min(falta, Math.max(0, t[i]));
    falta -= partes[i];
  }
  return partes.map(reais);
}
